import { api, get, post } from '@/lib/api';
import type {
  LinhaDeIcDaRelease,
  LinhaDeRelease,
  Release,
  ReleaseListada,
  ResultadoDaReversao,
  SituacaoDeRelease,
  TipoDeRelease,
} from '@/features/releases/tipos';

/**
 * Acesso à API de Release Management.
 *
 * Uma função por rota de `releases.controller.ts`, sem nenhuma inventada. O que
 * falta está **anotado**, não simulado:
 *
 *   • **não há `PATCH /releases/:id`.** Nome, plano de implantação, plano de
 *     verificação, plano de retorno e data prevista são definidos na criação e
 *     não têm rota de alteração. A tela mostra esses campos como leitura depois
 *     de criados, em vez de oferecer um formulário que responderia 404;
 *
 *   • **`GET /releases` não aceita filtro.** O serviço sabe filtrar por
 *     situação e por período, o controller chama `list()` sem argumento. O
 *     recorte da lista é feito no cliente, sobre o que veio.
 */

const RAIZ = '/releases';

const remover = (caminho: string) => api<{ removed: boolean }>(caminho, { method: 'DELETE' });

export const listarReleases = () => get<ReleaseListada[]>(RAIZ);

export const obterRelease = (id: string) => get<Release>(`${RAIZ}/${id}`);

export interface NovaRelease {
  name: string;
  ownerId: string;
  description?: string;
  type?: TipoDeRelease;
  plannedDeployAt?: string;
  changeWindowId?: string;
  deploymentPlan?: string;
  verificationPlan?: string;
  rollbackPlan?: string;
}

/**
 * `code` não é aceito: ele sai do esquema de identificadores, com alocação
 * atômica. Deixar o cliente escolher devolveria a colisão que o mecanismo
 * existe para impedir.
 *
 * A resposta é a **linha crua**: só `GET /releases` pede `_count`. Prometer
 * `ReleaseListada` aqui armaria a cilada para quem escrevesse `setQueryData`
 * sobre a listagem — o cartão da lista lê `_count.changes` e receberia
 * `undefined`. Quem chama invalida a listagem em vez de costurar a resposta.
 */
export const criarRelease = (corpo: NovaRelease) => post<LinhaDeRelease>(RAIZ, corpo);

/** Move no ciclo. 409 com as saídas que existem quando não pode. */
export const moverRelease = (id: string, status: SituacaoDeRelease, version?: number) =>
  post<LinhaDeRelease>(`${RAIZ}/${id}/status`, {
    status,
    ...(version === undefined ? {} : { version }),
  });

export const cancelarRelease = (id: string, version?: number) =>
  post<LinhaDeRelease>(`${RAIZ}/${id}/cancel`, version === undefined ? {} : { version });

export const acrescentarMudanca = (id: string, ticketId: string, sequence?: number) =>
  post<{ releaseId: string; ticketId: string; sequence: number }>(`${RAIZ}/${id}/changes`, {
    ticketId,
    ...(sequence === undefined ? {} : { sequence }),
  });

export const retirarMudanca = (id: string, ticketId: string) =>
  remover(`${RAIZ}/${id}/changes/${ticketId}`);

export interface IcDaReleaseEntrada {
  ciId: string;
  versionBefore?: string | null;
  versionAfter?: string | null;
}

/**
 * Substitui a lista inteira. A resposta traz as linhas gravadas **sem** o item
 * de configuração associado — `findMany` sem `include` —, então o nome de cada
 * um vem de outro lugar; quem chama invalida a ficha em vez de usar isto para
 * desenhar.
 */
export const definirIcsDaRelease = (id: string, cis: readonly IcDaReleaseEntrada[]) =>
  post<LinhaDeIcDaRelease[]>(`${RAIZ}/${id}/cis`, { cis });

/**
 * ★ Reverte o conjunto — critério 4.14.
 *
 * Uma operação, numa transação: a versão vai para revertida e **cada** mudança
 * do conjunto é marcada, com auditoria e evento próprios. A resposta diz
 * quantas foram, e é isso que a tela mostra: sem esse número, "revertemos a
 * 2026.09" não diria o que aconteceu com as sete mudanças dentro dela.
 */
export const reverterRelease = (id: string, reason: string, version?: number) =>
  post<ResultadoDaReversao>(`${RAIZ}/${id}/rollback`, {
    reason,
    ...(version === undefined ? {} : { version }),
  });

export const excluirRelease = (id: string) =>
  api<void>(`${RAIZ}/${id}`, { method: 'DELETE' });

export const chavesDeRelease = {
  raiz: ['entregas'] as const,
  lista: ['entregas', 'lista'] as const,
  item: (id: string) => ['entregas', 'item', id] as const,
};
